/**
 * Browse Navigation v3 Prototype — Inline Expansion
 *
 * Concept: Basecamp / Documents / Product Board expand INLINE within the
 * workspace section instead of opening a separate bottom panel.
 * This keeps all context under one workspace header and eliminates the
 * repetition of workspace names across multiple panel regions.
 *
 * Key differences from v2 (browse-nav):
 *   - No PanelGroup / lower split panel
 *   - Clicking a mode (Basecamp, Documents) toggles an inline expansion
 *   - Single scrollable sidebar — workspace > mode > content hierarchy
 *   - GlobalOpenTabsSection still at top, but no bottom pane duplication
 *
 * Documents section UX (mock):
 *   - X-overflow: long file names are scrollable (overflow-x-auto) without widening the sidebar.
 *   - Folder focus: double-click a folder to "zoom in" so that folder becomes the top level
 *     within Documents only; workspace extensions and other workspaces stay visible.
 *     Breadcrumbs: path segments only; click Documents row to show full tree (no separate up control).
 *     Focus paths are sanitized to the tree so mixed/invalid paths don't corrupt the trail.
 *     Drill-down: tighter left padding (especially files); file rows use Tooltip for full filename.
 *     Breadcrumbs: plain text; parent segments use --ws-color hover (match ws-nav-item). Tree drill is instant until
 *     a flat-row / FLIP implementation can slide ancestors only without remounting the focused folder’s children.
 *
 * Access: http://flownotes.localhost/?test=browse-nav-inline
 * Module: `frontend/src/components/browse-nav-inline/browse-nav-inline-test.tsx`
 *
 * Shared for production:
 *   - `@/lib/documents-tree-path` — drill-down / sanitize / display roots
 *   - `@/lib/workspace-crumb-styles` — parent breadcrumb button classes (--ws-color)
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  Home,
  Building2,
  User,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  FolderOpen,
  Folder,
  FileText,
  LayoutGrid,
  Settings,
  CheckSquare,
  Boxes,
  Search,
  Bell,
  Trash2,
  Tag,
  Network,
  Plus,
  MoreHorizontal,
  Circle,
  Plug,
  Briefcase,
  FolderTree,
  Bookmark,
  User2,
  CalendarClock,
  X,
  AlignJustify,
  Inbox,
  Mic,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  type DocumentsTreeNode,
  sanitizeDocumentsFocusPath,
  getDocumentsDrillDownView,
} from '@/lib/documents-tree-path'
import { workspaceCrumbParentButtonClassName } from '@/lib/workspace-crumb-styles'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { BasecampIcon } from '@/components/icons/basecamp-icon'

// ─── Types & mock data ────────────────────────────────────────────────────────

type SidebarMode = 'browse' | 'search'

type ExtensionNode = {
  id: string
  label: string
  icon: React.ElementType
  badge?: number
}

type WorkspaceDef = {
  id: string
  name: string
  icon: React.ElementType
  color: string
  type: 'work' | 'personal' | 'client'
  extensions: ExtensionNode[]
  fileCount: number
}

const WORKSPACES: WorkspaceDef[] = [
  {
    id: 'hop-studios',
    name: 'Hop Studios',
    icon: Building2,
    color: '#6366f1',
    type: 'work',
    fileCount: 124,
    extensions: [
      { id: 'basecamp',      label: 'Basecamp',      icon: BasecampIcon, badge: 7 },
      { id: 'product-board', label: 'Product Board', icon: LayoutGrid },
    ],
  },
  {
    id: 'personal',
    name: 'Personal',
    icon: User,
    color: '#10b981',
    type: 'personal',
    fileCount: 38,
    extensions: [],
  },
  {
    id: 'gschool',
    name: 'G-School',
    icon: Briefcase,
    color: '#f59e0b',
    type: 'client',
    fileCount: 19,
    extensions: [
      { id: 'basecamp', label: 'Basecamp', icon: BasecampIcon, badge: 2 },
    ],
  },
]

const BC_NAV_ITEMS = [
  { id: 'bc-bookmarks', label: 'Bookmarks',  icon: Bookmark,      badge: undefined },
  { id: 'bc-mystuff',   label: 'My Stuff',   icon: User2,         badge: 90 },
  { id: 'bc-todos',     label: 'Todos',      icon: CheckSquare,   badge: 7 },
  { id: 'bc-projects',  label: 'Projects',   icon: Boxes,         badge: undefined },
  { id: 'bc-triage',    label: 'Triage',     icon: CalendarClock, badge: undefined },
] as const

const MOCK_TREE: Record<string, DocumentsTreeNode[]> = {
  'hop-studios': [
    { id: 'hs-clients', name: 'clients', type: 'folder', children: [
      { id: 'hs-ggsc', name: 'greater-good', type: 'folder', children: [
        { id: 'hs-ggsc-ggsc', name: 'ggsc', type: 'folder', children: [
          { id: 'hs-ggsc-note', name: '2026-03-20-ggsc-notes.md', type: 'file' },
        ] },
        { id: 'hs-ggsc-ggia', name: 'ggia', type: 'folder', children: [
          { id: 'hs-ggsc-long', name: '2025-08-15-email-communication-template-and-asana-project-analysis.md', type: 'file' },
        ] },
      ]},
      { id: 'hs-star', name: 'star', type: 'folder' },
      { id: 'hs-ucsf', name: 'ucsf', type: 'folder' },
    ]},
    { id: 'hs-internal', name: 'internal', type: 'folder', children: [
      { id: 'hs-todos', name: 'todos', type: 'folder' },
    ]},
  ],
  'personal': [
    { id: 'p-journal', name: 'journal', type: 'folder' },
    { id: 'p-vball',   name: 'volleyball', type: 'folder' },
    { id: 'p-index',   name: 'index.md', type: 'file' },
  ],
  'gschool': [
    { id: 'gs-meetings', name: 'meetings', type: 'folder' },
    { id: 'gs-readme',   name: 'G-School.md', type: 'file' },
  ],
}

type NavItem =
  | { kind: 'none' }
  | { kind: 'home' }
  | { kind: 'inbox' }
  | { kind: 'workspace-home'; workspaceId: string }
  | { kind: 'extension'; workspaceId: string; extensionId: string }
  | { kind: 'global'; id: 'notifications' | 'tags' | 'graph' | 'trash' }

function navKey(item: NavItem): string {
  switch (item.kind) {
    case 'none':            return ''
    case 'home':            return 'home'
    case 'inbox':           return 'inbox'
    case 'workspace-home':  return `ws-home-${item.workspaceId}`
    case 'extension': return `ext-${item.workspaceId}-${item.extensionId}`
    case 'global':    return `global-${item.id}`
  }
}

// ─── NavRow ───────────────────────────────────────────────────────────────────

interface NavRowProps {
  icon: React.ElementType
  label: string
  indent?: number
  active?: boolean
  badge?: number
  color?: string
  chevron?: 'right' | 'down' | null
  muted?: boolean
  onClick?: () => void
  onChevronClick?: () => void
  actions?: React.ReactNode
}

function NavRow({ icon: Icon, label, indent = 0, active = false, badge, color, chevron = null, muted = false, onClick, onChevronClick, actions }: NavRowProps) {
  return (
    <div
      className={cn(
        // ws-nav-item handles border, hover bg, active bg via --ws-color CSS var (set on .ws-nav-section)
        'ws-nav-item group flex items-center gap-1.5 pr-2 py-1 select-none text-sm',
        active ? 'ws-nav-item--active text-sidebar-foreground' : 'text-sidebar-foreground/70',
        muted && 'opacity-50',
      )}
      style={{ paddingLeft: `${6 + indent * 14}px` }}
      onClick={onClick}
    >
      {chevron !== null ? (
        <span
          className="flex-none w-4 h-4 flex items-center justify-center -ml-1 rounded hover:bg-sidebar-accent/60"
          onClick={onChevronClick ? (e) => { e.stopPropagation(); onChevronClick() } : undefined}
        >
          {chevron === 'down' ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </span>
      ) : <span className="flex-none w-4" />}
      <Icon className="flex-none w-4 h-4" style={color ? { color } : undefined} />
      <span className="flex-1 truncate leading-snug">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="flex-none text-[10px] font-medium bg-primary/15 text-primary rounded-full px-1.5 py-0.5 leading-none">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {actions && (
        <span className="flex-none opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>{actions}</span>
      )}
    </div>
  )
}

// ─── Inline folder tree (simplified) ─────────────────────────────────────────
// pathFromRoot: path segments from Documents root to this node (e.g. ['clients', 'greater-good'])
// Used for folder-focus: double-click sets focus to this path (Documents section only).

const FOLDER_CLICK_DELAY_MS = 280

function InlineFolderNode({
  node, depth = 0, pathFromRoot, wsColor, activeDocPath, onSelectPath, onFocusFolder, drilledDown = false,
}: {
  node: DocumentsTreeNode
  depth?: number
  pathFromRoot: string[]
  wsColor: string
  activeDocPath: string | null
  onSelectPath: (id: string) => void
  onFocusFolder?: (pathSegments: string[]) => void
  /** Folder-focus / drill-down: tighter left padding (esp. for file rows at shallow depth) */
  drilledDown?: boolean
}) {
  const [expanded, setExpanded] = useState(depth === 0 && node.type === 'folder')
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
  }, [])

  const hasChildren = node.type === 'folder'
  const isSelected = activeDocPath === node.id
  const Icon = node.type === 'folder' ? (expanded ? FolderOpen : Folder) : FileText
  const pathToThis = pathFromRoot

  const handleRowClick = () => {
    // Only delay selection for folders (double-click = focus); files select immediately
    if (node.type !== 'folder' || !onFocusFolder) {
      onSelectPath(node.id)
      return
    }
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null
      onSelectPath(node.id)
    }, FOLDER_CLICK_DELAY_MS)
  }

  const handleRowDoubleClick = (e: React.MouseEvent) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current)
      clickTimerRef.current = null
    }
    e.preventDefault()
    if (node.type === 'folder' && onFocusFolder) {
      onFocusFolder([...pathToThis])
    }
  }

  // Drill-down: pull rows left; files at depth 0 align almost flush with breadcrumb column
  const padLeft =
    drilledDown && node.type === 'file'
      ? 4 + depth * 12
      : drilledDown
        ? 6 + depth * 12
        : 8 + depth * 14

  const nameEl =
    node.type === 'file' ? (
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <span className="flex-1 min-w-0 whitespace-nowrap overflow-visible cursor-default">
            {node.name}
          </span>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-sm break-all text-left">
          {node.name}
        </TooltipContent>
      </Tooltip>
    ) : (
      <span className="flex-1 min-w-0 whitespace-nowrap overflow-visible">{node.name}</span>
    )

  return (
    <div>
      <div
        className={cn(
          'ws-nav-item flex min-w-max items-center pr-2 py-1 text-sm select-none',
          'transition-[color,background-color,box-shadow] duration-150 ease-out',
          isSelected ? 'ws-nav-item--active text-sidebar-foreground' : 'text-sidebar-foreground/70',
        )}
        style={{ paddingLeft: `${padLeft}px` }}
        onClick={handleRowClick}
        onDoubleClick={handleRowDoubleClick}
        title={node.type === 'folder' ? 'Double-click to focus on this folder' : undefined}
      >
        {/* Chevron — expand/collapse only, does NOT navigate */}
        {hasChildren ? (
          <span
            className="flex-none w-4 h-4 flex items-center justify-center mr-1 rounded hover:bg-sidebar-accent shrink-0"
            onClick={e => { e.stopPropagation(); setExpanded(v => !v) }}
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </span>
        ) : (
          <span className={cn('flex-none mr-1 shrink-0', drilledDown && depth === 0 ? 'w-2' : 'w-4')} />
        )}
        <Icon className="flex-none w-3.5 h-3.5 mr-1.5 shrink-0" style={{ color: wsColor + 'aa' }} />
        {nameEl}
      </div>
      {expanded && node.children && (
        <div>
          {node.children.map(child => (
            <InlineFolderNode
              key={child.id}
              node={child}
              depth={depth + 1}
              pathFromRoot={[...pathFromRoot, child.name]}
              wsColor={wsColor}
              activeDocPath={activeDocPath}
              onSelectPath={onSelectPath}
              onFocusFolder={onFocusFolder}
              drilledDown={drilledDown}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Global Open Tabs ─────────────────────────────────────────────────────────

type GlobalTabType = 'note' | 'work-spec' | 'voice-memo' | 'basecamp-view' | 'folder-view'

type GlobalTab = {
  id: string
  title: string
  type: GlobalTabType
  workspaceId: string
  dirty?: boolean
}

const ALL_MOCK_TABS: GlobalTab[] = [
  { id: 'gt-1', title: 'Weekly Triage',             type: 'basecamp-view', workspaceId: 'hop-studios' },
  { id: 'gt-2', title: 'Wide ad management system', type: 'work-spec',     workspaceId: 'hop-studios' },
  { id: 'gt-3', title: 'clients/',                  type: 'folder-view',   workspaceId: 'hop-studios' },
  { id: 'gt-4', title: 'Tasks: Rowan',              type: 'note',          workspaceId: 'personal' },
  { id: 'gt-5', title: 'Faculty engagement todos',  type: 'work-spec',     workspaceId: 'gschool' },
]

function GlobalOpenTabsSection({ activeWsIds }: { activeWsIds: Set<string> }) {
  const [collapsed, setCollapsed] = useState(false)
  const [activeTabId, setActiveTabId] = useState<string | null>('gt-1')
  const [closedIds, setClosedIds] = useState<Set<string>>(new Set())

  const visibleTabs = ALL_MOCK_TABS.filter(t => !closedIds.has(t.id))

  const iconFor = (type: GlobalTabType) => {
    if (type === 'work-spec')     return <FileText className="h-3.5 w-3.5 text-blue-400 flex-none" />
    if (type === 'voice-memo')    return <Mic className="h-3.5 w-3.5 text-purple-400 flex-none" />
    if (type === 'basecamp-view') return <BasecampIcon className="h-3.5 w-3.5 flex-none text-muted-foreground" />
    if (type === 'folder-view')   return <Folder className="h-3.5 w-3.5 text-muted-foreground flex-none" />
    return <FileText className="h-3.5 w-3.5 text-muted-foreground flex-none" />
  }

  const closeAllForWs = (wsId: string) => {
    const toClose = visibleTabs.filter(t => t.workspaceId === wsId).map(t => t.id)
    setClosedIds(prev => new Set([...prev, ...toClose]))
    if (toClose.includes(activeTabId ?? '')) setActiveTabId(null)
  }

  const groups = WORKSPACES
    .filter(ws => activeWsIds.has(ws.id) && visibleTabs.some(t => t.workspaceId === ws.id))
    .map(ws => ({ ws, tabs: visibleTabs.filter(t => t.workspaceId === ws.id) }))

  return (
    <div data-testid="inline-open-tabs" className="flex-shrink-0 border-b border-border bg-sidebar">
      <div className="flex items-center gap-1 px-2 py-1.5">
        <button
          onClick={() => setCollapsed(c => !c)}
          className="flex items-center gap-1 flex-1 min-w-0 transition-colors text-sidebar-foreground/60 hover:text-sidebar-foreground"
        >
          {collapsed ? <ChevronRight className="h-3 w-3 flex-none" /> : <ChevronDown className="h-3 w-3 flex-none" />}
          <span className="text-[10px] font-semibold uppercase tracking-wider">Open Tabs</span>
        </button>
        <span className="text-[10px] text-sidebar-foreground/40 flex-none">{visibleTabs.length}</span>
      </div>

      {!collapsed && (
        <div className="pb-1">
          {groups.map(({ ws, tabs }) => {
            const WsIcon = ws.icon
            const dimmed = !activeWsIds.has(ws.id)
            return (
              <div
                key={ws.id}
                className="ws-tab-group group/tabgroup"
                style={{ '--ws-color': ws.color } as React.CSSProperties}
              >
                {/* Tab group header with hover actions */}
                <div className="ws-tab-group__header flex items-center gap-1.5 px-2 py-0.5 mt-0.5">
                  <span className="w-3.5 h-3.5 rounded flex items-center justify-center flex-none" style={{ backgroundColor: ws.color + '25' }}>
                    <WsIcon className="w-2 h-2" style={{ color: ws.color }} />
                  </span>
                  <span className={cn('text-[9px] font-semibold uppercase tracking-wider truncate flex-1', dimmed ? 'text-muted-foreground/50' : 'text-muted-foreground/70')}>
                    {ws.name}
                  </span>
                  {/* Actions — visible on group hover */}
                  <span className="flex items-center gap-0.5 opacity-0 group-hover/tabgroup:opacity-100 transition-opacity flex-none">
                    <button
                      title="New blank document"
                      onClick={() => alert(`New blank doc in ${ws.name}`)}
                      className="p-0.5 rounded hover:bg-sidebar-accent text-muted-foreground hover:text-sidebar-foreground transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                    <button
                      title="Close all in this workspace"
                      onClick={() => closeAllForWs(ws.id)}
                      className="p-0.5 rounded hover:bg-sidebar-accent text-muted-foreground hover:text-sidebar-foreground transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                </div>
                {tabs.map(tab => {
                  const isActive = activeTabId === tab.id
                  return (
                    <div
                      key={tab.id}
                      onClick={() => setActiveTabId(tab.id)}
                      className={cn(
                        'ws-nav-item group flex items-center gap-1.5 pr-2 py-1.5 text-sm',
                        isActive ? 'ws-nav-item--active text-sidebar-foreground' : 'text-sidebar-foreground/70',
                      )}
                      style={{ paddingLeft: '20px' }}
                    >
                      <button
                        onClick={e => { e.stopPropagation(); setClosedIds(prev => new Set([...prev, tab.id])) }}
                        className={cn('flex-none p-0.5 rounded transition-opacity', isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      {iconFor(tab.type)}
                      <span className="flex-1 truncate">{tab.title}</span>
                    </div>
                  )
                })}
              </div>
            )
          })}
          {visibleTabs.length === 0 && <p className="text-[11px] text-muted-foreground/50 px-3 py-1 italic">No open tabs</p>}
        </div>
      )}
    </div>
  )
}

// ─── WorkspaceSection (inline expansion) ─────────────────────────────────────

/** Per-workspace Documents focus path. Empty = show full tree. Enables "focus on folder" (double-click). */
type DocumentsFocusPath = Record<string, string[]>

interface WorkspaceSectionProps {
  workspace: WorkspaceDef
  expandedWorkspaces: Set<string>
  expandedSections: Set<string>  // keys: `${wsId}::${extId}` or `${wsId}::docs`
  documentsFocusPath: DocumentsFocusPath
  activeNav: NavItem
  activeDocPath: string | null
  onToggleWorkspace: (id: string) => void
  onToggleSection: (wsId: string, sectionId: string) => void
  onNavigate: (item: NavItem) => void
  onSelectDocPath: (id: string) => void
  onFocusDocumentsFolder: (workspaceId: string, pathSegments: string[]) => void
  onHideWorkspace: (id: string) => void
}

function WorkspaceSection({ workspace, expandedWorkspaces, expandedSections, documentsFocusPath, activeNav, activeDocPath, onToggleWorkspace, onToggleSection, onNavigate, onSelectDocPath, onFocusDocumentsFolder, onHideWorkspace }: WorkspaceSectionProps) {
  const WsIcon = workspace.icon
  const wsExpanded = expandedWorkspaces.has(workspace.id)
  const sectionKey = (id: string) => `${workspace.id}::${id}`
  const isSectionExpanded = (id: string) => expandedSections.has(sectionKey(id))
  const isActive = (extId: string) => navKey(activeNav) === `ext-${workspace.id}-${extId}`
  const focusPath = documentsFocusPath[workspace.id] ?? []
  const treeForWs = useMemo(() => MOCK_TREE[workspace.id] ?? [], [workspace.id])

  const focusPathKey = focusPath.join('\0')

  // Repair invalid focus paths (e.g. mixed branches) so tree + breadcrumbs stay in sync
  useEffect(() => {
    const safe = sanitizeDocumentsFocusPath(treeForWs, focusPath)
    if (safe.length !== focusPath.length || safe.some((s, i) => s !== focusPath[i])) {
      onFocusDocumentsFolder(workspace.id, safe)
    }
  }, [workspace.id, focusPathKey, treeForWs, focusPath, onFocusDocumentsFolder])

  // Context menu state
  const [ctxOpen, setCtxOpen] = useState(false)
  const ctxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!ctxOpen) return
    const handler = (e: MouseEvent) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target as Node)) setCtxOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [ctxOpen])

  return (
    <div
      data-testid={`inline-workspace-${workspace.id}`}
      className={cn('ws-nav-section mb-0.5', wsExpanded && 'pb-3 mb-2')}
      style={{ '--ws-color': workspace.color } as React.CSSProperties}
    >
      {/* Workspace header row */}
      <div
        className={cn(
          'ws-nav-section-header group flex items-center gap-2 px-2 py-1 select-none',
          wsExpanded ? 'ws-nav-section-header--expanded text-sidebar-foreground' : 'text-sidebar-foreground/70',
        )}
      >
        {/* Chevron — expand/collapse only */}
        <span
          className="flex-none w-3 h-3 flex items-center justify-center text-muted-foreground rounded hover:bg-sidebar-accent/60 cursor-pointer"
          onClick={() => onToggleWorkspace(workspace.id)}
        >
          {wsExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </span>
        {/* Icon pill + name — click opens workspace home dashboard */}
        <span
          className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
          onClick={() => onNavigate({ kind: 'workspace-home', workspaceId: workspace.id })}
        >
          <span className="flex-none w-7 h-7 rounded flex items-center justify-center" style={{ backgroundColor: workspace.color + '30' }}>
            <WsIcon className="w-4 h-4" style={{ color: workspace.color }} />
          </span>
          <span className="flex-1 truncate text-[15px] font-semibold leading-snug">{workspace.name}</span>
        </span>

        {/* ⋯ context menu trigger */}
        <span
          ref={ctxRef}
          className="flex-none relative opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={e => { e.stopPropagation(); setCtxOpen(o => !o) }}
        >
          <button className={cn('p-0.5 rounded transition-colors', ctxOpen ? 'bg-sidebar-accent text-sidebar-foreground' : 'hover:bg-sidebar-accent')}>
            <MoreHorizontal className="w-3 h-3" />
          </button>

          {ctxOpen && (
            <div className="absolute right-0 top-full mt-1 z-50 w-36 rounded-md border border-border bg-popover shadow-lg py-1 text-sm">
              <button
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-sidebar-accent transition-colors text-sidebar-foreground"
                onClick={e => { e.stopPropagation(); setCtxOpen(false); alert(`Manage: ${workspace.name}`) }}
              >
                <Settings className="w-3.5 h-3.5 text-muted-foreground" />
                Manage
              </button>
              <button
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-sidebar-accent transition-colors text-sidebar-foreground"
                onClick={e => { e.stopPropagation(); setCtxOpen(false); onHideWorkspace(workspace.id) }}
              >
                <ChevronLeft className="w-3.5 h-3.5 text-muted-foreground" />
                Hide
              </button>
            </div>
          )}
        </span>
      </div>

      {/* Workspace content — inline expansion per mode */}
      {wsExpanded && (
        <div>
          {workspace.extensions.map(ext => {
            const ExtIcon = ext.icon
            const expanded = isSectionExpanded(ext.id)
            const active = isActive(ext.id)
            const isBasecamp = ext.id === 'basecamp'

            return (
              <div key={ext.id}>
                {/* Mode selector row — chevron toggles section, row click navigates */}
                <NavRow
                  icon={ExtIcon}
                  label={ext.label}
                  indent={1}
                  badge={ext.badge}
                  active={active}
                  color={workspace.color}
                  chevron={expanded ? 'down' : 'right'}
                  onChevronClick={() => onToggleSection(workspace.id, ext.id)}
                  onClick={() => onNavigate({ kind: 'extension', workspaceId: workspace.id, extensionId: ext.id })}
                />

                {/* Inline Basecamp content */}
                {expanded && isBasecamp && (
                  <div
                    className="ws-nav-inline-group"
                  >
                    {BC_NAV_ITEMS.map(item => {
                      const ItemIcon = item.icon
                      return (
                        <div
                          key={item.id}
                          onClick={() => onNavigate({ kind: 'extension', workspaceId: workspace.id, extensionId: item.id })}
                          className={cn(
                            'ws-nav-item flex items-center gap-2 pr-2 py-1.5 text-sm -ml-px',
                            navKey(activeNav) === `ext-${workspace.id}-${item.id}`
                              ? 'ws-nav-item--active text-sidebar-foreground'
                              : 'text-sidebar-foreground/70'
                          )}
                          style={{ paddingLeft: '12px' }}
                        >
                          <ItemIcon className="w-4 h-4 flex-none" />
                          <span className="flex-1 truncate">{item.label}</span>
                          {item.badge !== undefined && (
                            <span className="flex-none text-[11px] font-medium rounded-full px-1.5 py-0.5 leading-none"
                              style={{ backgroundColor: workspace.color + '20', color: workspace.color }}>
                              {item.badge > 99 ? '99+' : item.badge}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Inline non-Basecamp extension (e.g. Product Board — placeholder) */}
                {expanded && !isBasecamp && (
                  <div
                    className="ml-6 border-l py-1 px-3"
                    style={{ borderColor: workspace.color + '50' }}
                  >
                    {['Now', 'Next', 'Later'].map(section => (
                      <div
                        key={section}
                        onClick={() => onNavigate({ kind: 'extension', workspaceId: workspace.id, extensionId: section.toLowerCase() })}
                        className="flex items-center gap-2 py-1.5 text-sm cursor-pointer text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
                      >
                        <Circle className="w-2.5 h-2.5 flex-none" />
                        <span>{section}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}

          {workspace.extensions.length === 0 && (
            <div className="flex items-center gap-1.5 py-1 text-xs text-muted-foreground cursor-pointer hover:text-sidebar-foreground transition-colors" style={{ paddingLeft: `${8 + 14}px` }}>
              <Plug className="w-3 h-3" /><span>Add extension</span>
            </div>
          )}

          {/* Documents mode — inline expansion; optional folder focus + breadcrumbs (scope: Documents only) */}
          {(() => {
            const docsExpanded = isSectionExpanded('docs')
            const { displayRoots, pathPrefix } = getDocumentsDrillDownView(treeForWs, focusPath)
            return (
              <div>
                <NavRow
                  icon={FolderOpen}
                  label="Documents"
                  indent={1}
                  active={docsExpanded}
                  color={workspace.color}
                  chevron={docsExpanded ? 'down' : 'right'}
                  onChevronClick={() => onToggleSection(workspace.id, 'docs')}
                  onClick={() => {
                    if (focusPath.length > 0) {
                      onFocusDocumentsFolder(workspace.id, [])
                    }
                    onNavigate({ kind: 'extension', workspaceId: workspace.id, extensionId: 'docs' })
                  }}
                />
                {docsExpanded && (
                  <div className="ws-nav-inline-group">
                    {/* Breadcrumbs when focused — reset via Documents row above (no separate up control) */}
                    {focusPath.length > 0 && (
                      <div
                        className="mb-1 flex flex-wrap items-baseline gap-y-0.5 px-2 py-1 text-[11px]"
                        role="navigation"
                        aria-label="Folder path"
                      >
                        {focusPath.map((seg, i) => {
                          const pathUpToHere = focusPath.slice(0, i + 1)
                          const isLast = i === focusPath.length - 1
                          return (
                            <span key={`bc-${workspace.id}-${i}-${seg}`} className="inline-flex items-baseline gap-0.5">
                              <span className="text-muted-foreground/40 select-none" aria-hidden>
                                /
                              </span>
                              {isLast ? (
                                <span
                                  aria-current="location"
                                  className="max-w-[min(100%,12rem)] truncate text-xs font-medium text-sidebar-foreground"
                                  title={seg}
                                >
                                  {seg}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => onFocusDocumentsFolder(workspace.id, pathUpToHere)}
                                  title={`Focus ${pathUpToHere.join(' / ')}`}
                                  className={workspaceCrumbParentButtonClassName}
                                >
                                  {seg}
                                </button>
                              )}
                            </span>
                          )
                        })}
                      </div>
                    )}
                    {/* No whole-list opacity/slide: swapping roots remounts children. Ancestor-only slide needs flat rows + shared keys / FLIP — see file header. */}
                    <div className="min-w-0 overflow-x-auto overflow-y-visible">
                      {displayRoots.length > 0 ? (
                        displayRoots.map(node => (
                          <InlineFolderNode
                            key={node.id}
                            node={node}
                            depth={0}
                            pathFromRoot={[...pathPrefix, node.name]}
                            wsColor={workspace.color}
                            activeDocPath={activeDocPath}
                            onSelectPath={onSelectDocPath}
                            onFocusFolder={(pathSegments) => onFocusDocumentsFolder(workspace.id, pathSegments)}
                            drilledDown={focusPath.length > 0}
                          />
                        ))
                      ) : (
                        <p className="px-3 py-1 text-sm italic text-muted-foreground/60">
                          {focusPath.length > 0 ? 'Empty folder' : 'No documents'}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })()}
        </div>
      )}
      {/* Faint separator after expanded workspace */}
      {wsExpanded && <hr className="ws-nav-section-separator" />}
    </div>
  )
}

// ─── BrowsePanel ──────────────────────────────────────────────────────────────

function BrowsePanel({ activeNav, expandedWorkspaces, expandedSections, documentsFocusPath, activeWsIds, activeDocPath, onNavigate, onToggleWorkspace, onToggleSection, onSelectDocPath, onFocusDocumentsFolder, onHideWorkspace }: {
  activeNav: NavItem
  expandedWorkspaces: Set<string>
  expandedSections: Set<string>
  documentsFocusPath: DocumentsFocusPath
  activeWsIds: Set<string>
  activeDocPath: string | null
  onNavigate: (item: NavItem) => void
  onToggleWorkspace: (id: string) => void
  onToggleSection: (wsId: string, sectionId: string) => void
  onSelectDocPath: (id: string) => void
  onFocusDocumentsFolder: (workspaceId: string, pathSegments: string[]) => void
  onHideWorkspace: (id: string) => void
}) {
  const activeKey = navKey(activeNav)
  const visibleWorkspaces = WORKSPACES.filter(w => activeWsIds.has(w.id))

  return (
    <div data-testid="inline-browse-panel" className="flex flex-col h-full overflow-hidden">
      <div className="px-3 py-2 border-b border-border flex-shrink-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Browse</p>
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 px-2 py-2 space-y-0.5">
        <NavRow icon={Home} label="Home" active={activeKey === 'home'} onClick={() => onNavigate({ kind: 'home' })} />
        <NavRow icon={Inbox} label="Inbox" badge={3} active={activeKey === 'inbox'} onClick={() => onNavigate({ kind: 'inbox' })} />

        <div className="px-2 pt-3 pb-1 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Workspaces</span>
          <button className="p-0.5 rounded hover:bg-sidebar-accent text-muted-foreground hover:text-sidebar-foreground"><Plus className="w-3 h-3" /></button>
        </div>

        {visibleWorkspaces.map(ws => (
          <WorkspaceSection
            key={ws.id}
            workspace={ws}
            expandedWorkspaces={expandedWorkspaces}
            expandedSections={expandedSections}
            documentsFocusPath={documentsFocusPath}
            activeNav={activeNav}
            activeDocPath={activeDocPath}
            onToggleWorkspace={onToggleWorkspace}
            onToggleSection={onToggleSection}
            onNavigate={onNavigate}
            onSelectDocPath={onSelectDocPath}
            onFocusDocumentsFolder={onFocusDocumentsFolder}
            onHideWorkspace={onHideWorkspace}
          />
        ))}

        <div className="px-2 pt-3 pb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tools</span>
        </div>
        <NavRow icon={Bell} label="Notifications" badge={3} active={activeKey === 'global-notifications'} onClick={() => onNavigate({ kind: 'global', id: 'notifications' })} />
        <NavRow icon={Tag} label="Tags" active={activeKey === 'global-tags'} onClick={() => onNavigate({ kind: 'global', id: 'tags' })} />
        <NavRow icon={Network} label="Graph" active={activeKey === 'global-graph'} onClick={() => onNavigate({ kind: 'global', id: 'graph' })} />
        <NavRow icon={Trash2} label="Trash" active={activeKey === 'global-trash'} onClick={() => onNavigate({ kind: 'global', id: 'trash' })} />
      </div>
    </div>
  )
}

// ─── Mock data for dashboards ────────────────────────────────────────────────

const MOCK_RECENT_DOCS = [
  { id: 'd1', title: 'Wide Ad Management System',  workspace: 'Hop Studios', wsColor: '#6366f1', type: 'work-spec', updated: '2h ago' },
  { id: 'd2', title: 'Faculty Engagement Todos',    workspace: 'G-School',   wsColor: '#f59e0b', type: 'work-spec', updated: '5h ago' },
  { id: 'd3', title: 'Weekly Scrum Notes',          workspace: 'Hop Studios', wsColor: '#6366f1', type: 'note',      updated: 'Yesterday' },
  { id: 'd4', title: 'Monthly Budget Review',       workspace: 'Personal',   wsColor: '#10b981', type: 'note',      updated: '2 days ago' },
  { id: 'd5', title: 'CloudFlare Viability Report', workspace: 'Hop Studios', wsColor: '#6366f1', type: 'work-spec', updated: '3 days ago' },
]

const MOCK_OVERDUE = [
  { id: 'bc1', title: 'Review meta pixel privacy settings', project: 'Star', due: 'Mar 1', wsColor: '#6366f1' },
  { id: 'bc2', title: 'Provide social sign-on recommendations', project: 'GGIA', due: 'Mar 3', wsColor: '#6366f1' },
]

// ─── Home Dashboard ───────────────────────────────────────────────────────────

function HomeDashboard({ activeWsIds }: { activeWsIds: Set<string> }) {
  const visibleWorkspaces = WORKSPACES.filter(w => activeWsIds.has(w.id))
  const totalFiles = visibleWorkspaces.reduce((sum, w) => sum + w.fileCount, 0)

  return (
    <div className="h-full overflow-y-auto bg-background">
      {/* Header */}
      <div className="px-8 pt-8 pb-6 border-b border-border">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Good afternoon, Rowan</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {visibleWorkspaces.length} active workspace{visibleWorkspaces.length !== 1 ? 's' : ''} · {totalFiles} documents
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60 border border-dashed border-border rounded-md px-3 py-1.5">
            <LayoutGrid className="w-3 h-3" />
            <span>Customize dashboard</span>
          </div>
        </div>

        {/* Workspace cards */}
        <div className="flex gap-3 mt-5">
          {visibleWorkspaces.map(ws => {
            const WsIcon = ws.icon
            return (
              <div
                key={ws.id}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-border bg-background hover:bg-accent/40 transition-colors cursor-pointer"
                style={{ borderLeftWidth: 3, borderLeftColor: ws.color }}
              >
                <span className="w-6 h-6 rounded flex items-center justify-center flex-none" style={{ backgroundColor: ws.color + '25' }}>
                  <WsIcon className="w-3.5 h-3.5" style={{ color: ws.color }} />
                </span>
                <div>
                  <p className="text-sm font-medium leading-none">{ws.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{ws.fileCount} docs</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="px-8 py-6 grid grid-cols-3 gap-6">
        {/* Recent documents */}
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Recent Documents</h2>
            <span className="text-xs text-muted-foreground/60 border border-dashed border-border rounded px-2 py-0.5 cursor-pointer hover:text-muted-foreground transition-colors">
              Widget: Recent Activity
            </span>
          </div>
          <div className="space-y-1">
            {MOCK_RECENT_DOCS.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors cursor-pointer group">
                <FileText className="w-4 h-4 flex-none text-muted-foreground/60" />
                <span className="flex-1 text-sm truncate">{doc.title}</span>
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: doc.wsColor + '20', color: doc.wsColor }}>
                  {doc.workspace}
                </span>
                <span className="text-xs text-muted-foreground/50 flex-none opacity-0 group-hover:opacity-100 transition-opacity">{doc.updated}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-5">
          {/* Overdue todos widget */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500 flex-none" />
                Overdue
              </h2>
              <span className="text-xs text-muted-foreground/60 border border-dashed border-border rounded px-2 py-0.5 cursor-pointer hover:text-muted-foreground transition-colors">
                Widget: Basecamp Bookmarks
              </span>
            </div>
            <div className="space-y-1">
              {MOCK_OVERDUE.map(todo => (
                <div key={todo.id} className="flex items-start gap-2 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors cursor-pointer">
                  <CheckSquare className="w-3.5 h-3.5 flex-none text-red-400 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs leading-snug truncate">{todo.title}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{todo.project} · Due {todo.due}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick actions widget */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold">Quick Actions</h2>
              <span className="text-xs text-muted-foreground/60 border border-dashed border-border rounded px-2 py-0.5 cursor-pointer hover:text-muted-foreground transition-colors">
                Widget: Quick Actions
              </span>
            </div>
            <div className="space-y-1.5">
              {[
                { icon: Plus, label: 'New document' },
                { icon: Inbox, label: 'Open inbox' },
                { icon: Network, label: 'View graph' },
              ].map(({ icon: Icon, label }) => (
                <button key={label} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left hover:bg-accent/50 transition-colors text-muted-foreground hover:text-foreground">
                  <Icon className="w-3.5 h-3.5 flex-none" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard builder callout */}
      <div className="mx-8 mb-8 p-4 rounded-lg border border-dashed border-border bg-muted/20">
        <div className="flex items-center gap-3">
          <LayoutGrid className="w-5 h-5 flex-none text-muted-foreground/60" />
          <div>
            <p className="text-sm font-medium">Dashboard is customizable</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Widgets can be added, removed, and reordered. Planned: Recent Activity, Basecamp Bookmarks, Smart Suggestions, Quick Actions, Board View, AI Flows.
              See <span className="underline decoration-dotted cursor-pointer">Dashboard Widget Builder</span> bet for full spec.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Workspace Home Dashboard ─────────────────────────────────────────────────

function WorkspaceHomeDashboard({ workspaceId }: { workspaceId: string }) {
  const ws = WORKSPACES.find(w => w.id === workspaceId)
  if (!ws) return null
  const WsIcon = ws.icon
  const wsDocs = MOCK_RECENT_DOCS.filter(d => d.workspace === ws.name)
  const tree = MOCK_TREE[ws.id] ?? []

  return (
    <div className="h-full overflow-y-auto bg-background">
      {/* Header with workspace branding */}
      <div className="px-8 pt-8 pb-6 border-b border-border" style={{ borderTopWidth: 3, borderTopColor: ws.color }}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-lg flex items-center justify-center flex-none" style={{ backgroundColor: ws.color + '25' }}>
              <WsIcon className="w-5 h-5" style={{ color: ws.color }} />
            </span>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{ws.name}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">{ws.fileCount} documents · {ws.extensions.length} extension{ws.extensions.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60 border border-dashed border-border rounded-md px-3 py-1.5">
            <LayoutGrid className="w-3 h-3" />
            <span>Customize</span>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 grid grid-cols-3 gap-6">
        {/* Recent docs for this workspace */}
        <div className="col-span-2 space-y-5">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold">Recent in {ws.name}</h2>
              <span className="text-xs text-muted-foreground/60 border border-dashed border-border rounded px-2 py-0.5">
                Widget: Recent Activity
              </span>
            </div>
            {wsDocs.length > 0 ? (
              <div className="space-y-1">
                {wsDocs.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors cursor-pointer group">
                    <FileText className="w-4 h-4 flex-none text-muted-foreground/60" />
                    <span className="flex-1 text-sm truncate">{doc.title}</span>
                    <span className="text-xs text-muted-foreground/50 opacity-0 group-hover:opacity-100 transition-opacity">{doc.updated}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground/50 italic px-3">No recent documents in this workspace.</p>
            )}
          </div>

          {/* Folder overview */}
          {tree.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold">Folders</h2>
                <span className="text-xs text-muted-foreground/60 border border-dashed border-border rounded px-2 py-0.5">
                  Widget: File List
                </span>
              </div>
              <div className="space-y-1">
                {tree.map(node => (
                  <div key={node.id} className="flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors cursor-pointer">
                    <Folder className="w-4 h-4 flex-none" style={{ color: ws.color + 'cc' }} />
                    <span className="text-sm">{node.name}</span>
                    {node.children && <span className="text-xs text-muted-foreground/50 ml-auto">{node.children.length} items</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: extensions + stats */}
        <div className="space-y-5">
          {/* Extensions */}
          {ws.extensions.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold">Extensions</h2>
              </div>
              <div className="space-y-1.5">
                {ws.extensions.map(ext => {
                  const ExtIcon = ext.icon
                  return (
                    <div key={ext.id} className="flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors cursor-pointer border border-border">
                      <ExtIcon className="w-4 h-4 flex-none text-muted-foreground" />
                      <span className="flex-1 text-sm">{ext.label}</span>
                      {ext.badge !== undefined && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ backgroundColor: ws.color + '25', color: ws.color }}>
                          {ext.badge}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Placeholder board widget callout */}
          <div className="p-3 rounded-lg border border-dashed border-border bg-muted/20 text-xs text-muted-foreground/70">
            <p className="font-medium mb-1">Workspace dashboard</p>
            <p>Customizable per workspace. Planned widgets: Board View, Basecamp Progress, File List, AI Flows.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Content panel ────────────────────────────────────────────────────────────

function ContentPanel({ nav, activeWsIds }: { nav: NavItem; activeWsIds: Set<string> }) {
  if (nav.kind === 'home') {
    return <HomeDashboard activeWsIds={activeWsIds} />
  }
  if (nav.kind === 'workspace-home') {
    return <WorkspaceHomeDashboard workspaceId={nav.workspaceId} />
  }
  if (nav.kind === 'extension') {
    const ws = WORKSPACES.find(w => w.id === nav.workspaceId)
    const bc = BC_NAV_ITEMS.find(i => i.id === nav.extensionId)
    const extLabel = bc?.label ?? ws?.extensions.find(e => e.id === nav.extensionId)?.label ?? nav.extensionId
    return (
      <div className="p-6 max-w-2xl">
        <div className="flex items-center gap-2 mb-1 text-xs text-muted-foreground">
          <span style={{ color: ws?.color }}>{ws?.name}</span>
          <span>›</span>
          <span>{extLabel}</span>
        </div>
        <h1 className="text-xl font-bold mb-3">{extLabel}</h1>
        <p className="text-sm text-muted-foreground">Content for {extLabel} would load here.</p>
      </div>
    )
  }
  return (
    <div className="p-6">
      <p className="text-sm text-muted-foreground capitalize">{nav.kind} view</p>
    </div>
  )
}

// ─── Search panel (simplified) ────────────────────────────────────────────────

function SearchPanel() {
  const [query, setQuery] = useState('')
  return (
    <div data-testid="inline-search-panel" className="flex flex-col h-full overflow-hidden">
      <div className="px-3 py-2 border-b border-border flex-shrink-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Search</p>
      </div>
      <div className="px-3 py-2 border-b border-border flex-shrink-0">
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search documents…"
          className="w-full bg-sidebar-accent rounded px-3 py-1.5 text-sm outline-none placeholder:text-muted-foreground/60 border border-border focus:border-primary/50"
          autoFocus
        />
      </div>
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-muted-foreground/50">Type to search</p>
      </div>
    </div>
  )
}

// ─── Main prototype ───────────────────────────────────────────────────────────

export function BrowseNavInlineTest() {
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('browse')
  const [activeNav, setActiveNavRaw] = useState<NavItem>({ kind: 'home' })
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Set<string>>(new Set(['hop-studios']))
  // expandedSections: `${wsId}::${sectionId}` — e.g. 'hop-studios::basecamp', 'hop-studios::docs'
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['hop-studios::basecamp', 'hop-studios::docs']))
  const [wsPopoverOpen, setWsPopoverOpen] = useState(false)
  const [activeWsIds, setActiveWsIds] = useState<Set<string>>(new Set(['hop-studios', 'personal']))
  // Shared active doc path — only ONE folder/file active at a time across all workspaces
  const [activeDocPath, setActiveDocPathRaw] = useState<string | null>(null)
  // Documents folder focus (per workspace): double-click a folder to "zoom in"; scope is Documents section only
  const [documentsFocusPath, setDocumentsFocusPath] = useState<DocumentsFocusPath>({})
  const wsPopoverRef = useRef<HTMLDivElement>(null)

  const applyDocumentsFocus = useCallback((workspaceId: string, pathSegments: string[]) => {
    setDocumentsFocusPath(prev => {
      const tree = MOCK_TREE[workspaceId] ?? []
      const safe = pathSegments.length === 0 ? [] : sanitizeDocumentsFocusPath(tree, pathSegments)
      return { ...prev, [workspaceId]: safe }
    })
  }, [])

  // Mutually exclusive: selecting a nav item clears the doc path, and vice versa
  const setActiveNav = (item: NavItem) => { setActiveNavRaw(item); setActiveDocPathRaw(null) }
  const setActiveDocPath = (path: string) => { setActiveDocPathRaw(path); setActiveNavRaw({ kind: 'none' } as NavItem) }

  const toggleWs = (id: string) => setExpandedWorkspaces(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const toggleSection = (wsId: string, sectionId: string) => {
    const key = `${wsId}::${sectionId}`
    setExpandedSections(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n })
  }

  const toggleActiveWs = (id: string) => {
    setActiveWsIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
    // Collapse any expanded sections for that workspace when toggled off
    setExpandedSections(prev => {
      const n = new Set(prev)
      for (const key of n) { if (key.startsWith(`${id}::`)) n.delete(key) }
      return n
    })
  }

  // Close popover on outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (wsPopoverRef.current && !wsPopoverRef.current.contains(e.target as Node)) setWsPopoverOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const modeButtons: { mode: SidebarMode; icon: React.ElementType; title: string }[] = [
    { mode: 'browse', icon: FolderTree, title: 'Browse' },
    { mode: 'search', icon: Search,    title: 'Search' },
  ]

  return (
    <div data-testid="inline-root" className="h-screen flex bg-background text-foreground overflow-hidden">

      {/* ── Icon bar ──────────────────────────────────────────────────────── */}
      <div data-testid="inline-icon-bar" className="w-12 flex-none flex flex-col bg-sidebar-accent border-r border-border">

        {/* Org switcher */}
        <div ref={wsPopoverRef} className="relative">
          <button
            data-testid="inline-org-switcher"
            onClick={() => setWsPopoverOpen(o => !o)}
            title="Switch workspace"
            className={cn(
              'w-12 h-12 flex items-center justify-center border-b border-border transition-colors',
              wsPopoverOpen ? 'bg-sidebar text-sidebar-foreground' : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/80'
            )}
          >
            <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center relative">
              <span className="text-[9px] font-bold text-primary">FN</span>
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-primary text-[8px] text-primary-foreground flex items-center justify-center font-bold leading-none">
                {activeWsIds.size}
              </span>
            </div>
          </button>

          {wsPopoverOpen && (
            <div data-testid="inline-org-popover" className="absolute left-full top-0 ml-1 z-50 w-56 rounded-lg border border-border bg-popover shadow-xl py-2">
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Organizations</div>
              {WORKSPACES.map(ws => {
                const WsIcon = ws.icon
                const isActive = activeWsIds.has(ws.id)
                return (
                  <button
                    key={ws.id}
                    onClick={() => toggleActiveWs(ws.id)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-sidebar-accent text-left transition-colors group"
                  >
                    <span className={cn('w-4 h-4 rounded border flex items-center justify-center flex-none transition-colors', isActive ? 'bg-primary border-primary' : 'border-border')}>
                      {isActive && <span className="text-primary-foreground text-[10px]">✓</span>}
                    </span>
                    <WsIcon className="w-4 h-4 flex-none" style={{ color: ws.color }} />
                    <span className="flex-1 text-sm truncate">{ws.name}</span>
                    <Settings className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                )
              })}
              <div className="mx-3 my-1.5 border-t border-border" />
              <button className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <Plus className="w-3.5 h-3.5" />Create workspace
              </button>
              <button className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <AlignJustify className="w-3.5 h-3.5" />Manage organizations…
              </button>
            </div>
          )}
        </div>

        {/* Mode buttons */}
        <div className="flex flex-col items-center pt-1">
          {modeButtons.map(({ mode, icon: Icon, title }) => (
            <button
              key={mode}
              onClick={() => setSidebarMode(mode)}
              title={title}
              className={cn(
                'w-12 h-12 flex items-center justify-center transition-colors',
                sidebarMode === mode
                  ? 'bg-sidebar text-sidebar-foreground border-l-2 border-l-primary'
                  : 'text-sidebar-foreground/60 hover:bg-sidebar-accent/80 hover:text-sidebar-foreground'
              )}
            >
              <Icon className="w-4 h-4" />
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <button data-testid="inline-settings-btn" title="Settings" className="w-12 h-12 flex items-center justify-center text-sidebar-foreground/40 hover:text-sidebar-foreground transition-colors border-t border-border">
          <Settings className="w-4 h-4" />
        </button>
      </div>

      {/* ── Sidebar — single panel, no bottom split ───────────────────────── */}
      <div
        data-testid="inline-sidebar"
        className="w-64 flex-none bg-sidebar border-r border-border overflow-hidden flex flex-col"
        style={{ '--ws-color': 'hsl(var(--primary))' } as React.CSSProperties}
      >

        {/* Open tabs at top */}
        <GlobalOpenTabsSection activeWsIds={activeWsIds} />

        {/* Browse / Search panel fills remaining space */}
        <div className="flex-1 overflow-hidden min-h-0">
          {sidebarMode === 'browse' ? (
            <BrowsePanel
              activeNav={activeNav}
              expandedWorkspaces={expandedWorkspaces}
              expandedSections={expandedSections}
              documentsFocusPath={documentsFocusPath}
              activeWsIds={activeWsIds}
              activeDocPath={activeDocPath}
              onNavigate={setActiveNav}
              onToggleWorkspace={toggleWs}
              onToggleSection={toggleSection}
              onSelectDocPath={setActiveDocPath}
              onFocusDocumentsFolder={applyDocumentsFocus}
              onHideWorkspace={toggleActiveWs}
            />
          ) : (
            <SearchPanel />
          )}
        </div>
      </div>

      {/* ── Content area ─────────────────────────────────────────────────── */}
      <div data-testid="inline-content-area" className="flex-1 overflow-y-auto">
        <ContentPanel nav={activeNav} activeWsIds={activeWsIds} />
      </div>
    </div>
  )
}
